/* ============================================================================
   kitsters-wishlist-element.js — <kitsters-wishlist> and <kitsters-wishlist-count>
   ============================================================================
   The wishlist on India is Kalles': a custom element in their custom.js backed
   by a localStorage id list, with the "wishlist page" being a SEARCH over those
   ids (/search?view=wishlist&q=id:1 OR id:2 …) rather than a stored list. That
   design is Kitsters' and worth keeping; the implementation is Kalles' and is
   not ours to copy, so this is written fresh against the same contract.

   THE CONTRACT IS ATTRIBUTES AND CLASSES, NOT THE TAG NAME
   Every wishlist rule in the ported stylesheets targets
   `.product__wishlist[action="added"]` and friends — class plus attribute, never
   the element name. So keeping `class`, `action`, `data-id` and the two heart
   SVGs means ~45 existing selectors keep working with no CSS rewrite at all.
   That is why this file is small: only the behaviour had to be rebuilt.

   WHAT CHANGED, DELIBERATELY
   - Storage key is ours (`kitsters:wishlist`), not the vendor's namespace.
   - The `.kw-wis-slot` span is gone. It existed for Kalles to inject its own
     icon into and had to be hidden by CSS on our side; with no injection there
     is nothing to hide.
   - `action` reflects state as `add` | `added`, matching what the CSS expects.
     The India build also used `remove` for the wishlist page's × button; that
     is kept so `[action="remove"]` rules still match.

   §10.51 APPLIES TO THE HEART TOGGLE
   The two hearts are swapped with `display`, not the `hidden` attribute. That is
   deliberate: `[hidden]` is a type-level UA rule (0,0,1) and any class rule that
   sets `display` beats it, so a `hidden`-based toggle would leave both hearts
   painted while `el.hidden` cheerfully reported true. The stylesheets already
   own this via `[action]` selectors; this file only sets the attribute.
   ========================================================================== */
(function () {
  'use strict';

  var KEY = 'kitsters:wishlist';
  var LIMIT = 50;                       // India caps at 50 without the app; same here
  var EVT = 'kitsters:wishlist:change';

  function read() {
    try {
      var raw = localStorage.getItem(KEY);
      if (!raw) return [];
      return raw.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    } catch (e) {
      // Private mode and blocked site-data both throw on access, not on write.
      // A wishlist that cannot persist should still not break the page.
      return [];
    }
  }

  function write(ids) {
    if (ids.length > LIMIT) ids = ids.slice(0, LIMIT);
    try { localStorage.setItem(KEY, ids.join(',')); } catch (e) { /* see read() */ }
    document.dispatchEvent(new CustomEvent(EVT, { detail: { ids: ids, count: ids.length } }));
  }

  function has(id) { return read().indexOf(String(id)) !== -1; }

  function toggle(id) {
    var ids = read(), i = ids.indexOf(String(id));
    if (i === -1) ids.unshift(String(id)); else ids.splice(i, 1);
    write(ids);
    return i === -1;                    // true when it was just added
  }

  function remove(id) {
    var ids = read().filter(function (x) { return x !== String(id); });
    write(ids);
    return ids;
  }

  /** The wishlist "page" is a search over the saved ids. */
  function searchUrl(ids) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (!ids.length) return root + 'search?view=wishlist&q=';
    var q = ids.map(function (id) { return 'id:' + id; }).join(' OR ');
    return root + 'search?view=wishlist&q=' + encodeURIComponent(q);
  }

  class KitstersWishlist extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;
      this.setAttribute('role', this.getAttribute('role') || 'button');
      if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
      this.sync();
      this.addEventListener('click', this.onActivate.bind(this));
      this.addEventListener('keydown', function (e) {
        // A div-like element with role=button must answer Enter and Space itself.
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          this.onActivate(e);
        }
      }.bind(this));
      document.addEventListener(EVT, this.sync.bind(this));
    }

    get productId() { return this.getAttribute('data-id'); }

    /** Reflect stored state onto the attribute the stylesheets read. */
    sync() {
      if (this.getAttribute('action') === 'remove') return;   // the × button is fixed
      var on = has(this.productId);
      this.setAttribute('action', on ? 'added' : 'add');
      this.setAttribute('aria-pressed', on ? 'true' : 'false');
    }

    onActivate(e) {
      e.preventDefault();
      e.stopPropagation();              // never let the card's whole-card click fire too

      if (this.getAttribute('action') === 'remove') {
        var left = remove(this.productId);
        if (this.hasAttribute('remove-on-page')) {
          // On the wishlist page the list IS the search query, so the honest way
          // to reflect a removal is to re-run the search rather than splice the
          // DOM and leave the result count lying.
          window.location.href = searchUrl(left);
        } else {
          var item = this.closest('[data-product-id]');
          if (item) item.remove();
        }
        return;
      }

      toggle(this.productId);
      this.sync();
    }
  }

  /** Live count, e.g. the header's "Wishlist (3)". */
  class KitstersWishlistCount extends HTMLElement {
    connectedCallback() {
      this.render();
      document.addEventListener(EVT, this.render.bind(this));
    }
    render() {
      var n = read().length;
      // The element owns its own "(n)" wrapper so the sole call site - the
      // mobile profile card's "Wishlist (n)" - shows NOTHING at zero instead
      // of bare "()" (owner screenshot 2026-08-28). Empty rather than "(0)":
      // a count is only printed when there is something to count.
      this.textContent = n ? '(' + n + ')' : '';
    }
  }

  /** Header/profile wishlist LINK wrapper. The markup has always been
      <kitsters-wishlist-a><a href="/search?view=wishlist">…</a></kitsters-wishlist-a>
      but until 2026-08-28 this element was DEFINED NOWHERE - an undefined
      custom element is an inert inline wrapper, so the link navigated to the
      bare view with no q. The wishlist "page" is a SEARCH over the saved ids;
      with no q the search never runs and the page shows its empty state
      whatever the shopper saved. That was the owner's "wishlist is not
      working": saving worked, the page could never show it. This keeps the
      inner <a>'s href synced to the id query, on connect and on every change. */
  class KitstersWishlistA extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;
      this.syncHref();
      document.addEventListener(EVT, this.syncHref.bind(this));
    }
    syncHref() {
      var a = this.querySelector('a[href*="view=wishlist"], a');
      if (a) a.setAttribute('href', searchUrl(read()));
    }
  }

  if (!customElements.get('kitsters-wishlist')) customElements.define('kitsters-wishlist', KitstersWishlist);
  if (!customElements.get('kitsters-wishlist-count')) customElements.define('kitsters-wishlist-count', KitstersWishlistCount);
  if (!customElements.get('kitsters-wishlist-a')) customElements.define('kitsters-wishlist-a', KitstersWishlistA);

  window.KitstersWishlist = { read: read, has: has, toggle: toggle, remove: remove, searchUrl: searchUrl, EVENT: EVT };
})();
